/**
 * Portal Management Page
 *
 * Create and customize an online store portal that lists all active products.
 * Includes theme customization, navigation setup, and custom domain configuration.
 */

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import type { PortalSettings, PortalTheme, PortalNavigationItem } from '../types/form';

interface Portal {
  id: string;
  name: string;
  description?: string;
  settings: PortalSettings;
  created_at: string;
  updated_at: string;
}

interface Product {
  id: string;
  name: string;
  slug: string;
  status: 'draft' | 'published';
  price?: number;
  currency?: string;
  image?: string;
}

const defaultTheme: PortalTheme = {
  primaryColor: '#0d9488',
  secondaryColor: '#6366f1',
  backgroundColor: '#ffffff',
  textColor: '#1f2937',
  accentColor: '#f59e0b',
  fontFamily: 'Inter',
  headerStyle: 'standard',
  footerStyle: 'standard',
  productCardStyle: 'detailed',
  borderRadius: 'medium',
};

const defaultPortalSettings: PortalSettings = {
  enabled: true,
  name: 'My Store',
  theme: defaultTheme,
  layout: 'grid',
  productsPerPage: 12,
  showCategories: true,
  showSearch: true,
  showFilters: true,
};

export default function PortalPage() {
  const [portal, setPortal] = useState<Portal | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'products' | 'design' | 'navigation' | 'domain' | 'settings'>('products');

  const [portalSettings, setPortalSettings] = useState<PortalSettings>(defaultPortalSettings);

  // Load portal data
  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);

        // Try to load existing portal settings
        try {
          const portalResponse = await api.get('/api/portal');
          if (portalResponse.ok) {
            const portalData = await portalResponse.json() as Portal;
            setPortal(portalData);
            setPortalSettings(portalData.settings);
          }
        } catch {
          // No portal yet, use defaults
        }

        // Load products
        try {
          const productsResponse = await api.get('/api/forms?form_type=product&status=published');
          const formsData = await productsResponse.json() as { forms: Product[] };
          setProducts(formsData.forms || []);
        } catch {
          setProducts([]);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load portal');
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, []);

  // Save portal settings
  const handleSave = useCallback(async () => {
    try {
      setSaving(true);
      if (portal) {
        await api.put(`/api/portal/${portal.id}`, { settings: portalSettings });
      } else {
        const response = await api.post('/api/portal', { settings: portalSettings });
        const newPortal = await response.json() as Portal;
        setPortal(newPortal);
      }
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save portal');
    } finally {
      setSaving(false);
    }
  }, [portal, portalSettings]);

  // Add navigation item
  const handleAddNavItem = useCallback(() => {
    const newItem: PortalNavigationItem = {
      id: crypto.randomUUID(),
      label: 'New Link',
      url: '/',
      position: (portalSettings.navigationItems?.length || 0) + 1,
    };
    setPortalSettings({
      ...portalSettings,
      navigationItems: [...(portalSettings.navigationItems || []), newItem],
    });
  }, [portalSettings]);

  // Remove navigation item
  const handleRemoveNavItem = useCallback((id: string) => {
    setPortalSettings({
      ...portalSettings,
      navigationItems: portalSettings.navigationItems?.filter(item => item.id !== id) || [],
    });
  }, [portalSettings]);

  // Update navigation item
  const handleUpdateNavItem = useCallback((id: string, updates: Partial<PortalNavigationItem>) => {
    setPortalSettings({
      ...portalSettings,
      navigationItems: portalSettings.navigationItems?.map(item =>
        item.id === id ? { ...item, ...updates } : item
      ) || [],
    });
  }, [portalSettings]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link
              to="/formspace"
              className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </Link>
            <div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-teal-500 to-indigo-500 flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                  </svg>
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Store Portal</h1>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Customize your online store
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a
              href={portalSettings.customDomain ? `https://${portalSettings.customDomain}` : `/store/${portalSettings.subdomain || 'preview'}`}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
            >
              Preview Store
            </a>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
        <nav className="flex gap-4">
          {(['products', 'design', 'navigation', 'domain', 'settings'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab
                  ? 'border-teal-500 text-teal-600 dark:text-teal-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
        {/* Products Tab */}
        {activeTab === 'products' && (
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Store Products</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Published products will appear in your store
                </p>
              </div>
              <Link
                to="/formspace"
                className="px-4 py-2 border border-teal-500 text-teal-600 dark:text-teal-400 rounded-lg hover:bg-teal-50 dark:hover:bg-teal-900/20"
              >
                + Add Product
              </Link>
            </div>

            {products.length === 0 ? (
              <div className="text-center py-12 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg">
                <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
                <p className="text-gray-500 dark:text-gray-400">No published products yet</p>
                <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                  Create and publish products to add them to your store
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {products.map((product) => (
                  <Link
                    key={product.id}
                    to={`/formspace/products/${product.id}`}
                    className="group border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden hover:shadow-md transition-shadow"
                  >
                    <div className="aspect-square bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                      {product.image ? (
                        <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
                      ) : (
                        <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      )}
                    </div>
                    <div className="p-3">
                      <h4 className="text-sm font-medium text-gray-900 dark:text-white truncate group-hover:text-teal-600 dark:group-hover:text-teal-400">
                        {product.name}
                      </h4>
                      {product.price && (
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {product.currency || 'USD'} {product.price.toFixed(2)}
                        </p>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Design Tab */}
        {activeTab === 'design' && (
          <div className="p-6 space-y-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Store Design</h3>

            {/* Store Info */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Store Name
                </label>
                <input
                  type="text"
                  value={portalSettings.name}
                  onChange={(e) => setPortalSettings({ ...portalSettings, name: e.target.value })}
                  className="w-full input"
                  placeholder="My Store"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Store Description
                </label>
                <textarea
                  value={portalSettings.description || ''}
                  onChange={(e) => setPortalSettings({ ...portalSettings, description: e.target.value || undefined })}
                  className="w-full input"
                  rows={3}
                  placeholder="Describe your store..."
                />
              </div>
            </div>

            {/* Colors */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
              <h4 className="text-md font-medium text-gray-700 dark:text-gray-300 mb-4">Colors</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm text-gray-600 dark:text-gray-400 mb-2">Primary Color</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={portalSettings.theme.primaryColor}
                      onChange={(e) => setPortalSettings({
                        ...portalSettings,
                        theme: { ...portalSettings.theme, primaryColor: e.target.value }
                      })}
                      className="w-10 h-10 rounded border border-gray-200 dark:border-gray-600 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={portalSettings.theme.primaryColor}
                      onChange={(e) => setPortalSettings({
                        ...portalSettings,
                        theme: { ...portalSettings.theme, primaryColor: e.target.value }
                      })}
                      className="flex-1 input text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-gray-600 dark:text-gray-400 mb-2">Secondary Color</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={portalSettings.theme.secondaryColor}
                      onChange={(e) => setPortalSettings({
                        ...portalSettings,
                        theme: { ...portalSettings.theme, secondaryColor: e.target.value }
                      })}
                      className="w-10 h-10 rounded border border-gray-200 dark:border-gray-600 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={portalSettings.theme.secondaryColor}
                      onChange={(e) => setPortalSettings({
                        ...portalSettings,
                        theme: { ...portalSettings.theme, secondaryColor: e.target.value }
                      })}
                      className="flex-1 input text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-gray-600 dark:text-gray-400 mb-2">Accent Color</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={portalSettings.theme.accentColor}
                      onChange={(e) => setPortalSettings({
                        ...portalSettings,
                        theme: { ...portalSettings.theme, accentColor: e.target.value }
                      })}
                      className="w-10 h-10 rounded border border-gray-200 dark:border-gray-600 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={portalSettings.theme.accentColor}
                      onChange={(e) => setPortalSettings({
                        ...portalSettings,
                        theme: { ...portalSettings.theme, accentColor: e.target.value }
                      })}
                      className="flex-1 input text-sm"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Layout */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
              <h4 className="text-md font-medium text-gray-700 dark:text-gray-300 mb-4">Layout</h4>
              <div className="grid grid-cols-3 gap-4 mb-4">
                {(['grid', 'list', 'masonry'] as const).map((layout) => (
                  <button
                    key={layout}
                    onClick={() => setPortalSettings({ ...portalSettings, layout })}
                    className={`p-4 border-2 rounded-lg text-center transition-all ${
                      portalSettings.layout === layout
                        ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <span className="text-sm font-medium text-gray-900 dark:text-white capitalize">{layout}</span>
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-600 dark:text-gray-400 mb-2">Products Per Page</label>
                  <select
                    value={portalSettings.productsPerPage}
                    onChange={(e) => setPortalSettings({ ...portalSettings, productsPerPage: parseInt(e.target.value) })}
                    className="w-full input"
                  >
                    <option value={8}>8</option>
                    <option value={12}>12</option>
                    <option value={16}>16</option>
                    <option value={24}>24</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-600 dark:text-gray-400 mb-2">Product Card Style</label>
                  <select
                    value={portalSettings.theme.productCardStyle}
                    onChange={(e) => setPortalSettings({
                      ...portalSettings,
                      theme: { ...portalSettings.theme, productCardStyle: e.target.value as 'minimal' | 'detailed' | 'overlay' }
                    })}
                    className="w-full input"
                  >
                    <option value="minimal">Minimal</option>
                    <option value="detailed">Detailed</option>
                    <option value="overlay">Overlay</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Features */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
              <h4 className="text-md font-medium text-gray-700 dark:text-gray-300 mb-4">Features</h4>
              <div className="space-y-3">
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={portalSettings.showCategories}
                    onChange={(e) => setPortalSettings({ ...portalSettings, showCategories: e.target.checked })}
                    className="rounded border-gray-300 dark:border-gray-600 text-teal-600"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Show category navigation</span>
                </label>
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={portalSettings.showSearch}
                    onChange={(e) => setPortalSettings({ ...portalSettings, showSearch: e.target.checked })}
                    className="rounded border-gray-300 dark:border-gray-600 text-teal-600"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Show search bar</span>
                </label>
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={portalSettings.showFilters}
                    onChange={(e) => setPortalSettings({ ...portalSettings, showFilters: e.target.checked })}
                    className="rounded border-gray-300 dark:border-gray-600 text-teal-600"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Show product filters</span>
                </label>
              </div>
            </div>
          </div>
        )}

        {/* Navigation Tab */}
        {activeTab === 'navigation' && (
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Navigation Links</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Add custom links to your store header
                </p>
              </div>
              <button
                onClick={handleAddNavItem}
                className="px-4 py-2 border border-teal-500 text-teal-600 dark:text-teal-400 rounded-lg hover:bg-teal-50 dark:hover:bg-teal-900/20"
              >
                + Add Link
              </button>
            </div>

            {(!portalSettings.navigationItems || portalSettings.navigationItems.length === 0) ? (
              <div className="text-center py-12 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg">
                <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
                <p className="text-gray-500 dark:text-gray-400">No custom navigation links</p>
                <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                  Add links to pages, categories, or external sites
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {portalSettings.navigationItems.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
                  >
                    <svg className="w-5 h-5 text-gray-400 cursor-move" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                    </svg>
                    <input
                      type="text"
                      value={item.label}
                      onChange={(e) => handleUpdateNavItem(item.id, { label: e.target.value })}
                      className="flex-1 input text-sm"
                      placeholder="Link label"
                    />
                    <input
                      type="text"
                      value={item.url}
                      onChange={(e) => handleUpdateNavItem(item.id, { url: e.target.value })}
                      className="flex-1 input text-sm"
                      placeholder="URL"
                    />
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={item.isExternal}
                        onChange={(e) => handleUpdateNavItem(item.id, { isExternal: e.target.checked })}
                        className="rounded border-gray-300 dark:border-gray-600 text-teal-600"
                      />
                      <span className="text-xs text-gray-500 dark:text-gray-400">External</span>
                    </label>
                    <button
                      onClick={() => handleRemoveNavItem(item.id)}
                      className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Social Links */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-6 mt-6">
              <h4 className="text-md font-medium text-gray-700 dark:text-gray-300 mb-4">Social Links</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-600 dark:text-gray-400 mb-2">Facebook</label>
                  <input
                    type="url"
                    value={portalSettings.socialLinks?.facebook || ''}
                    onChange={(e) => setPortalSettings({
                      ...portalSettings,
                      socialLinks: { ...portalSettings.socialLinks, facebook: e.target.value || undefined }
                    })}
                    className="w-full input text-sm"
                    placeholder="https://facebook.com/..."
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 dark:text-gray-400 mb-2">Instagram</label>
                  <input
                    type="url"
                    value={portalSettings.socialLinks?.instagram || ''}
                    onChange={(e) => setPortalSettings({
                      ...portalSettings,
                      socialLinks: { ...portalSettings.socialLinks, instagram: e.target.value || undefined }
                    })}
                    className="w-full input text-sm"
                    placeholder="https://instagram.com/..."
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 dark:text-gray-400 mb-2">Twitter</label>
                  <input
                    type="url"
                    value={portalSettings.socialLinks?.twitter || ''}
                    onChange={(e) => setPortalSettings({
                      ...portalSettings,
                      socialLinks: { ...portalSettings.socialLinks, twitter: e.target.value || undefined }
                    })}
                    className="w-full input text-sm"
                    placeholder="https://twitter.com/..."
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 dark:text-gray-400 mb-2">YouTube</label>
                  <input
                    type="url"
                    value={portalSettings.socialLinks?.youtube || ''}
                    onChange={(e) => setPortalSettings({
                      ...portalSettings,
                      socialLinks: { ...portalSettings.socialLinks, youtube: e.target.value || undefined }
                    })}
                    className="w-full input text-sm"
                    placeholder="https://youtube.com/..."
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Domain Tab */}
        {activeTab === 'domain' && (
          <div className="p-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Custom Domain</h3>

            <div className="space-y-6">
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <h4 className="text-sm font-medium text-blue-800 dark:text-blue-300">Connect Your Domain</h4>
                    <p className="text-xs text-blue-700 dark:text-blue-400 mt-1">
                      Host your store on your own domain by adding a CNAME record pointing to our servers.
                    </p>
                  </div>
                </div>
              </div>

              {/* Subdomain */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Store Subdomain
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={portalSettings.subdomain || ''}
                    onChange={(e) => setPortalSettings({ ...portalSettings, subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') || undefined })}
                    className="flex-1 input"
                    placeholder="mystore"
                  />
                  <span className="text-gray-500 dark:text-gray-400">.xtform.app</span>
                </div>
                <p className="text-xs text-gray-500 mt-1">This will be your free store URL</p>
              </div>

              {/* Custom Domain */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Custom Domain
                </label>
                <input
                  type="text"
                  value={portalSettings.customDomain || ''}
                  onChange={(e) => setPortalSettings({ ...portalSettings, customDomain: e.target.value || undefined })}
                  className="w-full input"
                  placeholder="store.yourdomain.com"
                />
              </div>

              {portalSettings.customDomain && (
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">DNS Configuration</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    Add the following CNAME record to your domain's DNS settings:
                  </p>
                  <div className="bg-gray-900 text-gray-100 rounded-lg p-4 font-mono text-sm">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-gray-400">Type:</span>
                      <span>CNAME</span>
                    </div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-gray-400">Name:</span>
                      <span>{portalSettings.customDomain?.split('.')[0] || 'store'}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400">Value:</span>
                      <span>portal.xtform.app</span>
                    </div>
                  </div>

                  <div className="mt-4 flex items-center gap-2">
                    {portalSettings.cnameVerified ? (
                      <span className="flex items-center gap-2 text-green-600 dark:text-green-400 text-sm">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Domain verified
                      </span>
                    ) : (
                      <>
                        <span className="flex items-center gap-2 text-yellow-600 dark:text-yellow-400 text-sm">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Pending verification
                        </span>
                        <button className="text-sm text-teal-600 hover:underline">
                          Verify now
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="p-6 space-y-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Store Settings</h3>

            {/* SEO */}
            <div className="space-y-4">
              <h4 className="text-md font-medium text-gray-700 dark:text-gray-300">SEO</h4>
              <div>
                <label className="block text-sm text-gray-600 dark:text-gray-400 mb-2">SEO Title</label>
                <input
                  type="text"
                  value={portalSettings.seoTitle || ''}
                  onChange={(e) => setPortalSettings({ ...portalSettings, seoTitle: e.target.value || undefined })}
                  className="w-full input"
                  placeholder="Store title for search engines"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 dark:text-gray-400 mb-2">SEO Description</label>
                <textarea
                  value={portalSettings.seoDescription || ''}
                  onChange={(e) => setPortalSettings({ ...portalSettings, seoDescription: e.target.value || undefined })}
                  className="w-full input"
                  rows={3}
                  placeholder="Description for search engines (150-160 characters)"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 dark:text-gray-400 mb-2">SEO Keywords</label>
                <input
                  type="text"
                  value={portalSettings.seoKeywords || ''}
                  onChange={(e) => setPortalSettings({ ...portalSettings, seoKeywords: e.target.value || undefined })}
                  className="w-full input"
                  placeholder="keyword1, keyword2, keyword3"
                />
              </div>
            </div>

            {/* Analytics */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
              <h4 className="text-md font-medium text-gray-700 dark:text-gray-300 mb-4">Analytics</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-600 dark:text-gray-400 mb-2">Google Analytics ID</label>
                  <input
                    type="text"
                    value={portalSettings.googleAnalyticsId || ''}
                    onChange={(e) => setPortalSettings({ ...portalSettings, googleAnalyticsId: e.target.value || undefined })}
                    className="w-full input"
                    placeholder="G-XXXXXXXXXX"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 dark:text-gray-400 mb-2">Facebook Pixel ID</label>
                  <input
                    type="text"
                    value={portalSettings.facebookPixelId || ''}
                    onChange={(e) => setPortalSettings({ ...portalSettings, facebookPixelId: e.target.value || undefined })}
                    className="w-full input"
                    placeholder="XXXXXXXXXXXXXXX"
                  />
                </div>
              </div>
            </div>

            {/* Legal */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
              <h4 className="text-md font-medium text-gray-700 dark:text-gray-300 mb-4">Legal Pages</h4>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-600 dark:text-gray-400 mb-2">Terms of Service URL</label>
                  <input
                    type="url"
                    value={portalSettings.termsUrl || ''}
                    onChange={(e) => setPortalSettings({ ...portalSettings, termsUrl: e.target.value || undefined })}
                    className="w-full input"
                    placeholder="https://..."
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 dark:text-gray-400 mb-2">Privacy Policy URL</label>
                  <input
                    type="url"
                    value={portalSettings.privacyUrl || ''}
                    onChange={(e) => setPortalSettings({ ...portalSettings, privacyUrl: e.target.value || undefined })}
                    className="w-full input"
                    placeholder="https://..."
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 dark:text-gray-400 mb-2">Refund Policy URL</label>
                  <input
                    type="url"
                    value={portalSettings.refundPolicyUrl || ''}
                    onChange={(e) => setPortalSettings({ ...portalSettings, refundPolicyUrl: e.target.value || undefined })}
                    className="w-full input"
                    placeholder="https://..."
                  />
                </div>
              </div>
            </div>

            {/* Contact */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
              <h4 className="text-md font-medium text-gray-700 dark:text-gray-300 mb-4">Contact Information</h4>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-600 dark:text-gray-400 mb-2">Contact Email</label>
                  <input
                    type="email"
                    value={portalSettings.contactEmail || ''}
                    onChange={(e) => setPortalSettings({ ...portalSettings, contactEmail: e.target.value || undefined })}
                    className="w-full input"
                    placeholder="contact@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 dark:text-gray-400 mb-2">Contact Phone</label>
                  <input
                    type="tel"
                    value={portalSettings.contactPhone || ''}
                    onChange={(e) => setPortalSettings({ ...portalSettings, contactPhone: e.target.value || undefined })}
                    className="w-full input"
                    placeholder="+1 (555) 000-0000"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 dark:text-gray-400 mb-2">Address</label>
                  <textarea
                    value={portalSettings.contactAddress || ''}
                    onChange={(e) => setPortalSettings({ ...portalSettings, contactAddress: e.target.value || undefined })}
                    className="w-full input"
                    rows={2}
                    placeholder="123 Main St, City, Country"
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
